export const ROLES = [
  'super_admin',
  'admin',
  'mis',
  'bd',
  'ra',
  'qa',
  'qc',
  'fnd',
  'production_liquid',
  'production_osd',
  'production_sg',
  'rm_store',
  'pm_store',
  'design',
  'artwork',
  'ppic',
  'exporter',
] as const;

export type Role = (typeof ROLES)[number];
export type StaffRole = Exclude<Role, 'exporter'>;

export const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  mis: 'MIS',
  bd: 'Business Development',
  ra: 'Regulatory Affairs',
  qa: 'Quality Assurance',
  qc: 'Quality Control',
  fnd: 'Formulation & Development',
  production_liquid: 'Production – Liquid',
  production_osd: 'Production – OSD',
  production_sg: 'Production – Soft Gel',
  rm_store: 'RM Store',
  pm_store: 'PM Store',
  design: 'Design',
  artwork: 'Artwork',
  ppic: 'PPIC',
  exporter: 'Exporter',
};

export const DEPARTMENT_ROLES = [
  'qa',
  'qc',
  'fnd',
  'production_liquid',
  'production_osd',
  'production_sg',
  'rm_store',
  'pm_store',
  'design',
  'artwork',
  'ppic',
] as const satisfies readonly StaffRole[];

export const STAFF_ROLES = ROLES.filter((r): r is StaffRole => r !== 'exporter');

/**
 * Roles that run the document request flow in MExP: raise requests, allocate work, verify uploads and send the
 * dossier to BD. BD itself works in BDS (SC-NBD): it raises requests and reviews dossiers there, not in MExP.
 */
export const COORDINATOR_ROLES = ['super_admin', 'admin', 'mis', 'ra'] as const satisfies readonly StaffRole[];

/** Roles that may manage user accounts. Only super_admin may manage these roles themselves. */
export const USER_ADMIN_ROLES = ['super_admin', 'admin'] as const satisfies readonly StaffRole[];
export const PRIVILEGED_ROLES = ['super_admin', 'admin'] as const satisfies readonly StaffRole[];

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

export function isStaffRole(value: unknown): value is StaffRole {
  return isRole(value) && value !== 'exporter';
}

export function hasRole(role: Role | undefined | null, allowed: readonly Role[]): boolean {
  return !!role && allowed.includes(role);
}

const LEGACY_ROLE_ALIASES: Record<string, Role> = {
  'super admin': 'super_admin',
  superadmin: 'super_admin',
  'mis head': 'mis',
  'business development': 'bd',
  regulatory: 'ra',
  'regulatory affairs': 'ra',
  'qa head': 'qa',
  'qc head': 'qc',
  'fnd head': 'fnd',
  'f&d': 'fnd',
  designer: 'design',
  'design department': 'design',
  'artwork checking': 'artwork',
  'ppic department': 'ppic',
};

/**
 * Port of the legacy `normalizeRole_`: lower-case, map aliases, otherwise replace whitespace runs with `_`.
 * Returns null when the result is not one of the 17 canonical roles.
 */
export function normalizeRole(raw: unknown): Role | null {
  if (raw === null || raw === undefined) return null;
  const lower = String(raw).trim().toLowerCase();
  if (!lower) return null;
  const alias = LEGACY_ROLE_ALIASES[lower];
  if (alias) return alias;
  const candidate = lower.replace(/\s+/g, '_');
  return isRole(candidate) ? candidate : null;
}
